import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { makeStyles } from "@material-ui/core/styles";

import { PHOTO_STAGES, T } from "../constants";
import { useUi, cx } from "../ui";
import FieldRow, { DetailRow } from "../components/FieldRow";
import ModalCard from "../components/ModalCard";
import Plate from "../components/Plate";
import PhotoCaptureBlock from "../dashboard/components/PhotoCaptureBlock";
import useVehiclePhotos from "../dashboard/useVehiclePhotos";
import { valetRunnerLots } from "../dashboard/queries";
import { VEHICLE_PHOTO_MODES } from "../../../lib/conciergeFeature";

/*
 * The porter's own set, shot where the car ended up. Asked for the way the
 * concierge is asked at intake: the set is expected, and a runner who cannot
 * take it says so on its own button rather than finding the park unfileable.
 */
const PARKING_PHOTO_HINTS = {
  noBooking:
    "This car has no booking on file, so parking photos cannot be saved against it.",
  hydrating: "Loading any parking photos already taken for this car...",
  error: "A photo did not upload. Retry it, or confirm the park without it.",
  busy: "Uploading - keep this screen open until the photos finish.",
  allDone: "All angles captured where the car is parked.",
  idle:
    "Shoot the car where you left it - it dates the condition to the bay, not the forecourt.",
};

const useStyles = makeStyles({
  head: { display: "flex", alignItems: "center", gap: 10, marginBottom: 12 },
  select: { width: "100%" },
  note: { fontSize: 11, color: T.mutedFg, marginTop: 8 },
});

/*
 * The one thing only the runner knows: where the car actually ended up. The
 * guest card names a lot at intake, but a full lot or a closed gate sends the
 * runner somewhere else, so the lot is asked here rather than assumed - within
 * the kind of parking the guest bought. An offsite card's car belongs in an
 * offsite car park and an onsite card's car does not, so the card's lot opens
 * the field and only lots of its own kind are offered; the API refuses the
 * other kind anyway (VALET_LOT_TYPE_MISMATCH).
 */
export default function ParkCompletionModal({
  job,
  facilityId,
  confirming,
  onConfirm,
  onClose,
  vehiclePhotoMode = VEHICLE_PHOTO_MODES.OPTIONAL,
}) {
  const s = useStyles();
  const ui = useUi();

  const { data } = useQuery(valetRunnerLots, {
    variables: { facilityId },
    skip: !facilityId,
  });

  /* A card with no lot of its own constrains nothing, so every lot is offered. */
  const lots = (data?.valetRunnerLots ?? []).filter(
    (item) => !job.lotType || item.type === job.lotType,
  );
  const [lotId, setLotId] = useState(job.lotId ?? job.cardLotId ?? "");
  const [bay, setBay] = useState("");
  const [ticket, setTicket] = useState("");
  const [photosWaived, setPhotosWaived] = useState(false);
  const photosDisabled = vehiclePhotoMode === VEHICLE_PHOTO_MODES.DISABLED;
  const photosRequired = vehiclePhotoMode === VEHICLE_PHOTO_MODES.REQUIRED;

  /* Where the car ended up, shot at the bay. In optional mode a runner may
   * waive the set; required mode holds confirmation for it. An upload still
   * in flight always holds confirmation because closing aborts the upload. */
  const parking = useVehiclePhotos({
    bookingId: photosDisabled ? null : job.bookingId,
    facilityId,
    stage: PHOTO_STAGES.PARKING,
    valetSessionId: job.valetSessionId,
  });

  /* An offsite car is on the facility's own ticket, and wherever they put it -
   * "Level 2 Row C" as often as a number - so both are asked for, and neither
   * is a bay we could offer a list of.
   *
   * Only the lot is held out for, and the ticket an offsite car is left on:
   * those are what find the car again. Where in the lot it stands is worth
   * recording and never worth keeping a runner at the keyboard for. */
  const lot = lots.find((item) => item.id === lotId) ?? null;
  const offsite = lot?.type === "OFFSITE";
  const located = Boolean(lotId) && (!offsite || Boolean(ticket.trim()));
  const photosReady =
    photosDisabled || parking.allDone || (!photosRequired && photosWaived);
  const ready = located && photosReady && !parking.anyBusy;

  return (
    <ModalCard
      title="Confirm parked"
      subtitle={job.guestName}
      onClose={onClose}
      width={520}
      footerLeft={
        !photosDisabled &&
        !photosRequired &&
        !parking.allDone &&
        !photosWaived ? (
          <button
            type="button"
            className={ui.mutedBtn}
            disabled={parking.anyBusy}
            onClick={() => setPhotosWaived(true)}
          >
            Confirm without photos
          </button>
        ) : null
      }
      footerRight={
        <>
          <button type="button" className={ui.mutedBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={ui.primaryBtn}
            disabled={!ready || confirming}
            onClick={() =>
              onConfirm({
                lotId,
                bay: bay.trim() || null,
                offsiteTicket: offsite ? ticket : null,
              })
            }
          >
            {confirming
              ? "Confirming..."
              : parking.anyBusy
              ? "Uploading photos..."
              : "Confirm parked"}
          </button>
        </>
      }
    >
      <div className={s.head}>
        <Plate size={13}>{job.plate}</Plate>
        <span>{job.vehicle}</span>
      </div>

      <DetailRow label="Destination lot">
        {job.lotName ?? "Not named on the guest card"}
      </DetailRow>

      <FieldRow
        label="Parked in"
        htmlFor="park-lot"
      >
        <select
          id="park-lot"
          className={cx(ui.field, s.select)}
          value={lotId}
          onChange={(e) => setLotId(e.target.value)}
        >
          <option value="">Choose a car park</option>
          {lots.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </FieldRow>

      {offsite ? (
        <>
          <FieldRow
            label="Ticket number"
            hint="The stub the car park gave you. It is what gets the car back."
            htmlFor="park-ticket"
          >
            <input
              id="park-ticket"
              className={ui.field}
              value={ticket}
              onChange={(e) => setTicket(e.target.value)}
              placeholder="E.G. WK-1234"
            />
          </FieldRow>

          <FieldRow
            label="Location"
            hint="Optional. Where in their car park you left it."
            htmlFor="park-bay"
          >
            <input
              id="park-bay"
              className={ui.field}
              value={bay}
              onChange={(e) => setBay(e.target.value)}
              placeholder="E.G. LEVEL 2 ROW C"
            />
          </FieldRow>
        </>
      ) : (
        <FieldRow
          label="Bay"
          hint="Optional. Front-row bays start F-, stacked rear bays start R-."
          htmlFor="park-bay"
        >
          <input
            id="park-bay"
            className={ui.field}
            value={bay}
            onChange={(e) => setBay(e.target.value)}
            placeholder="E.G. F-14 OR R-06"
          />
        </FieldRow>
      )}

      {!photosDisabled && (
        <PhotoCaptureBlock
          eyebrow="Parking photos"
          hints={
            photosRequired
              ? {
                  ...PARKING_PHOTO_HINTS,
                  error: "A photo did not upload. Retry it before confirming the park.",
                }
              : PARKING_PHOTO_HINTS
          }
          bookingId={job.bookingId}
          photos={parking.photos}
          onCapture={parking.capture}
          onRetry={parking.retry}
          hydrating={parking.hydrating}
          anyBusy={parking.anyBusy}
          anyError={parking.anyError}
          required={photosRequired}
          framed={false}
        />
      )}

      {!photosDisabled && !photosRequired && photosWaived && !parking.allDone && (
        <p className={s.note}>
          Filing the park without the parking set. The intake photos stay the
          condition record.
        </p>
      )}
    </ModalCard>
  );
}
